<template>
  <!-- 線下站專用登入彈窗：帳號由代理發放，無註冊、忘記密碼與第三方登入 -->
  <dialog
    ref="dialog"
    class="agent-login-dialog"
    aria-labelledby="agent-login-title"
    @cancel.prevent="close"
    @close="onDialogClose"
    @click="onBackdropClick"
  >
    <div class="agent-login">
      <button class="agent-login-close" type="button" :aria-label="$t('Bao.close')" @click="close">
        ×
      </button>
      <img v-if="logo" class="agent-login-logo" :src="logo" :alt="brandName">
      <section class="agent-login-card">
        <!-- 帳號密碼登入 -->
        <form v-if="!sessionKey" @submit.prevent="submitLogin">
          <div class="agent-login-heading">
            <h1 id="agent-login-title">
              {{ $t('Bao.login') }}
            </h1>
          </div>
          <div class="agent-login-field">
            <label for="agent-account">{{ $t('Bao.username') }}</label>
            <input
              id="agent-account"
              v-model.trim="username"
              name="username"
              autocomplete="username"
              autocapitalize="none"
              spellcheck="false"
              :placeholder="$t('Bao.username_require')"
              required
            >
          </div>
          <div class="agent-login-field">
            <label for="agent-password">{{ $t('Bao.password') }}</label>
            <div class="agent-login-password">
              <input
                id="agent-password"
                v-model="password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                :placeholder="$t('Bao.password_require')"
                required
              >
              <button
                class="agent-login-reveal"
                type="button"
                :aria-label="$t('Bao.password')"
                :aria-pressed="String(showPassword)"
                @click="showPassword = !showPassword"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /><path class="eye-slash" d="m4 4 16 16" /></svg>
              </button>
            </div>
          </div>
          <button class="agent-login-submit" type="submit" :disabled="actionLoading || !username || !password">
            {{ $t('Bao.login') }} <span aria-hidden="true">→</span>
          </button>
          <p v-if="statusText" class="agent-login-status" role="status">
            {{ statusText }}
          </p>
        </form>

        <!-- 二次驗證（與一般登入彈窗相同流程） -->
        <form v-else @submit.prevent="submitAuthCode">
          <div class="agent-login-heading">
            <h1 id="agent-login-title">
              {{ $t('Bao.auth_modal_text_1') }}
            </h1>
          </div>
          <div class="agent-login-field">
            <label for="agent-auth-code">{{ $t('Bao.auth_modal_text_2') }}</label>
            <input
              id="agent-auth-code"
              v-model.trim="authenticatorCode"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              :placeholder="$t('Bao.auth_modal_text_2')"
              required
            >
          </div>
          <button class="agent-login-submit" type="submit" :disabled="actionLoading || authenticatorCode.length !== 6">
            {{ $t('Bao.auth_modal_text_3') }} <span aria-hidden="true">→</span>
          </button>
          <p v-if="statusText" class="agent-login-status" role="status">
            {{ statusText }}
          </p>
        </form>
      </section>
    </div>
  </dialog>
</template>

<script>
import { login, loginUseOptCode } from '@/api/user'
import { login as localLogin } from '@/utils/user'
import { getLoginErrorMessage } from '@/utils/tool'
import { mapGetters } from 'vuex'

export default {
  name: 'AgentLoginModal',
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      actionLoading: false,
      sessionKey: null,
      authenticatorCode: '',
      // dialog 位於 top layer，toast 會被遮住，錯誤訊息直接顯示在彈窗內
      statusText: ''
    }
  },
  computed: {
    ...mapGetters(['brand']),
    isLoginActive() {
      return this.$store.state.user.isLoginActive
    },
    logo() {
      return this.brand && this.brand.info ? this.brand.info.h5_image : ''
    },
    brandName() {
      return process.env.VUE_APP_BRAND_NAME
    }
  },
  watch: {
    isLoginActive(isOpen) {
      this.syncDialog(isOpen)
      if (!isOpen) this.resetForm()
    }
  },
  mounted() {
    this.syncDialog(this.isLoginActive)
  },
  beforeDestroy() {
    if (this.$refs.dialog && this.$refs.dialog.open) {
      this.$refs.dialog.close()
    }
  },
  methods: {
    syncDialog(isOpen) {
      const dialog = this.$refs.dialog
      if (!dialog) return
      if (isOpen && !dialog.open) {
        dialog.showModal()
      } else if (!isOpen && dialog.open) {
        dialog.close()
      }
    },
    // 所有關閉途徑（×、遮罩、Esc）都改 store，再由 watcher 關閉 dialog
    close() {
      this.$store.commit('SET_LOGIN_OPEN', false)
    },
    // 保險：dialog 若被其他方式關閉，同步回 store
    onDialogClose() {
      if (this.isLoginActive) {
        this.$store.commit('SET_LOGIN_OPEN', false)
      }
    },
    resetForm() {
      this.password = ''
      this.showPassword = false
      this.sessionKey = null
      this.authenticatorCode = ''
      this.statusText = ''
    },
    // 點擊彈窗外的遮罩關閉
    onBackdropClick(event) {
      if (event.target !== this.$refs.dialog) return
      const rect = this.$refs.dialog.getBoundingClientRect()
      const outside = event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top || event.clientY > rect.bottom
      if (outside) this.close()
    },
    submitLogin() {
      if (this.actionLoading) return
      this.actionLoading = true
      this.statusText = ''
      login({ username: this.username, password: this.password }).then(response => {
        const code = response.data.data.code
        if (code === 1) { // 二次認證，sessionKey 帶入後續
          this.sessionKey = response.data.data.session_key
          return
        }
        if (code === 0) {
          this.getTokenAndLogin(response)
        } else {
          this.statusText = getLoginErrorMessage(code) || this.$t('Bao.login_error2')
        }
      }).catch(error => {
        this.statusText = this.getLoginErrorText(error)
      }).finally(() => {
        this.actionLoading = false
      })
    },
    submitAuthCode() {
      if (this.actionLoading) return
      this.actionLoading = true
      this.statusText = ''
      loginUseOptCode({
        username: this.username,
        session_key: this.sessionKey,
        opt_code: this.authenticatorCode
      }).then(response => {
        if (response.data.data.code === 0) {
          this.getTokenAndLogin(response)
        } else {
          this.statusText = this.$t('Bao.auth_error_text')
        }
      }).catch(error => {
        this.statusText = (error.response && error.response.data.msg) || this.$t('Bao.auth_error_text')
      }).finally(() => {
        this.actionLoading = false
      })
    },
    // 錯誤碼對應與一般登入彈窗相同
    getLoginErrorText(error) {
      const data = error && error.response && error.response.data && error.response.data.data
      const errorCode = data && data.code ? data.code[0] : null
      switch (errorCode) {
        case '1':
          return this.$t('Bao.login_error1', { time: new Date(data.time[0] * 1000).toLocaleString() })
        case '2':
          return this.$t('Bao.login_error2')
        case '3':
          return this.$t('Bao.login_error3')
        case '4':
          return this.$t('Bao.login_error4')
        case '5':
          return this.$t('Bao.login_error5')
        default:
          return getLoginErrorMessage(errorCode) || 'An unknown error occurred. Please try again.'
      }
    },
    // 與一般登入彈窗相同的登入後流程
    getTokenAndLogin(response) {
      const tokenData = response.data.data.token
      const expiredTime = new Date().getTime() + (tokenData.expired_interval * 1000)
      localStorage.setItem('expired_time', expiredTime)
      localStorage.setItem('token', tokenData.token)
      this.$store.commit('SET_LOGIN_TO_EVENT', true)
      this.$store.commit('SET_LOGIN_OPEN', false)
      this.$store.commit('SET_READ_TERMS', false)

      let redirect = ''
      if (localStorage.getItem('redirectAfterLogin')) {
        redirect = this.$store.state.user.dailyItem.link
        localStorage.removeItem('redirectAfterLogin')
        this.$store.commit('SET_DAILY', false)
        this.$store.commit('SET_DAILY_ITEM', null)
        this.$store.commit('SET_LOGIN_TO_EVENT', false)
      } else if (this.$store.getters.inviteCode !== null) {
        redirect = 'user'
      }

      localLogin(response.data.data, redirect)
    }
  }
}
</script>

<style scoped>
/* 版面依設計稿 design_web/katsuwin/css/agent-login.css，顏色沿用主題變數 */
.agent-login-dialog, .agent-login-dialog * { box-sizing: border-box; }
.agent-login-dialog { padding: 0; border: 0; background: var(--bg-popupbox); color: var(--text-primary); width: 440px; max-width: calc(100vw - 32px); max-height: calc(100svh - 32px); border-radius: 24px; overflow: auto; box-shadow: 0 28px 100px #0005; }
.agent-login-dialog::backdrop { background: #10101999; backdrop-filter: blur(5px); }
.agent-login { position: relative; width: 100%; max-width: 440px; padding-top: 36px; font-family: Arial, "Noto Sans JP", sans-serif; }
.agent-login-close { position: absolute; top: 12px; right: 12px; display: grid; place-items: center; width: 40px; height: 40px; border: 0; border-radius: 50%; color: var(--text-secondary); background: transparent; font-size: 28px !important; }
.agent-login-close:hover { background: color-mix(in srgb, var(--text-primary) 8%, transparent); }
.agent-login-logo { display: block; width: 190px; height: 72px; object-fit: contain; margin: 0 auto; }
.agent-login-card { padding: 28px 40px 40px; border-radius: 24px; background: var(--bg-popupbox); }
.agent-login-heading { margin-bottom: 32px; }
.agent-login h1 { margin: 0; font-size: 28px; letter-spacing: .06em; }
.agent-login-field { margin-bottom: 24px; }
.agent-login label { display: block; margin-bottom: 10px; font-size: 13px; font-weight: 600; }
.agent-login input { width: 100%; height: 54px; border: 1px solid color-mix(in srgb, var(--text-secondary) 25%, transparent); border-radius: 12px; padding: 0 16px; background: var(--bg-primary); color: var(--text-primary); font: inherit; font-size: 16px; transition: border-color .2s, box-shadow .2s; }
.agent-login input::placeholder { color: var(--text-secondary); opacity: .8; font-size: 14px; }
.agent-login input:focus-visible { outline: none; border-color: var(--btn-primary-normal); box-shadow: 0 0 0 3px color-mix(in srgb, var(--btn-primary-normal) 18%, transparent); }
.agent-login-password { position: relative; }
.agent-login-password input { padding-right: 58px; }
.agent-login button { cursor: pointer; font: inherit; }
.agent-login-reveal { position: absolute; right: 5px; top: 5px; width: 44px; height: 44px; display: grid; place-items: center; border: 0; border-radius: 9px; color: var(--text-secondary); background: transparent; }
.agent-login-reveal:hover { color: var(--text-primary); background: color-mix(in srgb, var(--text-primary) 7%, transparent); }
.agent-login-reveal svg { width: 21px; height: 21px; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }
.agent-login-reveal .eye-slash { display: none; }
.agent-login-reveal[aria-pressed="true"] .eye-slash { display: block; }
.agent-login-submit { width: 100%; height: 54px; margin-top: 8px; display: flex; justify-content: center; align-items: center; gap: 18px; border: 0; border-radius: 12px; font-weight: 700 !important; color: var(--btn-text-dark-normal); background: var(--btn-primary-normal); transition: filter .2s; }
.agent-login-submit:hover:not(:disabled) { filter: brightness(1.07); }
.agent-login-submit:disabled { cursor: not-allowed; opacity: .6; }
.agent-login button:focus-visible { outline: 2px solid var(--btn-primary-normal); outline-offset: 4px; }
.agent-login-status { margin: 20px 0 0; font-size: 13px; line-height: 1.7; color: var(--text-secondary); }
@media (max-width: 600px) {
  .agent-login { max-width: none; padding-top: 30px; }
  .agent-login-logo { width: 166px; height: 64px; }
  .agent-login-card { padding: 24px; border-radius: 20px; }
  .agent-login h1 { font-size: 25px; }
}
@media (prefers-reduced-motion: reduce) { .agent-login * { transition: none !important; } }
</style>

<style>
body:has(.agent-login-dialog[open]) { overflow: hidden; }
</style>
